"use client";

import { Icon } from "@/components/ui/Icon";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { type Locale, localeFromPath } from "@/config/i18n";
import { type I18n, tr } from "@/config/localeText";

/**
 * Modal de recherche chargé à la demande — **le gros levier de poids JS du
 * site**.
 *
 * `SearchModal` importe `data/search/searchData`, qui importe à son tour les
 * registres de contenu complets : `chauffeurPages` (82 fiches ville), leurs
 * jumelles EN et AR, `excursionPages`, `RENTAL_VEHICLES`, `TRANSFER_ROUTES`…
 * `searchData` n'en extrait que des champs légers (url, titre, mots-clés), mais
 * l'extraction est faite à l'exécution : le bundler, lui, doit embarquer les
 * modules importés en entier. Le corpus éditorial trilingue se retrouvait donc
 * dans le chunk du layout racine — ~2,2 Mo décompressés téléchargés sur les
 * 711 pages du site, pour une palette de recherche que la plupart des visiteurs
 * n'ouvriront jamais.
 *
 * `ssr: false` + montage conditionnel sur `open` : le chunk n'est demandé qu'au
 * premier clic (ou premier ⌘K). Le composant est déjà écrit pour ça — il
 * retournait `null` tant que `open` était faux, et ses deux effets se nettoient
 * au démontage (restitution du focus, libération du scroll lock).
 */
const SearchModal = dynamic(
  () => import("./SearchModal").then((mod) => mod.SearchModal),
  { ssr: false },
);

const LABELS: I18n<{ button: string; aria: string }> = {
  fr: { button: "Rechercher", aria: "Ouvrir la recherche" },
  en: { button: "Search", aria: "Open search" },
  ar: { button: "بحث", aria: "فتح البحث" },
};

type Props = {
  /** Variante visuelle : barre desktop (avec libellé + ⌘K) ou ligne mobile. */
  variant?: "desktop" | "mobile";
  className?: string;
};

/**
 * Bouton de recherche + modal. Possède l'état d'ouverture ET le raccourci
 * global Cmd/Ctrl+K, pour garder le Header simple. Monté côté client
 * uniquement (rien dans le SSR qui casse l'hydratation : le modal n'est monté
 * qu'une fois ouvert — cf. le `dynamic()` ci-dessus).
 */
export function SearchTrigger({ variant = "desktop", className = "" }: Props) {
  const pathname = usePathname();
  const locale: Locale = localeFromPath(pathname ?? "/");
  const labels = tr(LABELS, locale);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  // Raccourci global Cmd/Ctrl+K (ouvre/bascule), indépendant du focus.
  // Porté UNIQUEMENT par la variante desktop : la variante mobile peut être
  // montée simultanément (menu burger ouvert) et on évite ainsi deux écouteurs
  // et deux modals empilés. Cmd/K reste une affordance desktop.
  useEffect(() => {
    if (variant !== "desktop") return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant]);

  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          aria-label={labels.aria}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
          className={`flex w-full items-center gap-2 rounded-lg border border-white/20 px-3 py-2 text-start text-white transition-colors hover:border-white/40 ${className}`}
        >
          <Icon icon="material-symbols:search" className="h-5 w-5" />
          <span className="font-semibold">{labels.button}</span>
        </button>
        {open && <SearchModal open onClose={close} />}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label={labels.aria}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-white/70 transition-colors hover:border-white/35 hover:text-white ${className}`}
      >
        <Icon icon="material-symbols:search" className="h-5 w-5" />
        {/* Libellé + ⌘K seulement à partir de 2xl : sur la bande xl (1280-1536px)
            le header est dense (3 onglets + À propos + Contact + CTA + langue),
            on garde la recherche en icône seule pour éviter le débordement. */}
        <span className="hidden 2xl:inline">{labels.button}</span>
        <kbd className="hidden items-center rounded border border-white/20 bg-white/10 px-1.5 py-0.5 font-sans text-[10px] text-white/60 2xl:inline-flex">
          ⌘K
        </kbd>
      </button>
      <SearchModal open={open} onClose={close} />
    </>
  );
}
